<template>
  <Teleport to="body">
    <div v-if="open" class="cancel-sub-overlay" @click.self="emitClose">
      <div class="cancel-sub-card" @click.stop>
        <header class="cancel-sub-head">
          <h2>Cancel Subscription</h2>
          <button type="button" class="cancel-sub-x" aria-label="Close" @click="emitClose">
            <img :src="imgClose" alt="" />
          </button>
        </header>

        <div class="cancel-sub-body">
          <p class="plan-line">Plan: {{ plan }}</p>

          <label class="field-label">Cancellation effective time:</label>
          <div class="select-wrap" @click.stop>
            <button
              type="button"
              class="select-trigger"
              :class="{ open: dropdownOpen }"
              @click="dropdownOpen = !dropdownOpen"
            >
              <span class="select-trigger-text">{{ selectedLabel }}</span>
              <img
                :src="downarrowIcon"
                alt=""
                class="select-arrow"
                :class="{ up: dropdownOpen }"
              />
            </button>
            <div v-if="dropdownOpen" class="select-menu">
              <button
                v-for="opt in options"
                :key="opt.value"
                type="button"
                class="select-option"
                :class="{ selected: selected === opt.value }"
                @click="selectOption(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>
        </div>

        <footer class="cancel-sub-actions">
          <button type="button" class="btn btn-cancel" @click="emitClose">
            <span class="btn-txt">Cancel</span>
          </button>
          <button type="button" class="btn btn-submit" @click="onSubmit">
            <span class="btn-txt">Submit</span>
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import imgClose from '@/assets/Fill 1@2x.png'
import downarrowIcon from '@/assets/downarrow@2x.png'

export type CancelEffectiveTime = 'end_of_cycle' | 'immediate'

const EFFECTIVE_OPTIONS: { value: CancelEffectiveTime; label: string }[] = [
  { value: 'end_of_cycle', label: 'At the end of the current service cycle' },
  { value: 'immediate', label: 'Effective immediately' },
]

const props = defineProps<{
  open: boolean
  plan: string
}>()

const emit = defineEmits<{
  close: []
  submit: [effectiveTime: CancelEffectiveTime]
}>()

const options = EFFECTIVE_OPTIONS
const defaultEffectiveTime: CancelEffectiveTime = 'end_of_cycle'
const defaultEffectiveLabel =
  EFFECTIVE_OPTIONS.find((option) => option.value === defaultEffectiveTime)?.label ??
  'At the end of the current service cycle'
const selected = ref<CancelEffectiveTime>(defaultEffectiveTime)
const dropdownOpen = ref(false)

const selectedLabel = ref(defaultEffectiveLabel)

watch(
  () => props.open,
  (open) => {
    if (!open) {
      dropdownOpen.value = false
      return
    }
    selected.value = defaultEffectiveTime
    selectedLabel.value = defaultEffectiveLabel
    dropdownOpen.value = false
  },
)

function selectOption(value: CancelEffectiveTime): void {
  selected.value = value
  selectedLabel.value = EFFECTIVE_OPTIONS.find((o) => o.value === value)?.label ?? ''
  dropdownOpen.value = false
}

function emitClose(): void {
  dropdownOpen.value = false
  emit('close')
}

function onSubmit(): void {
  emit('submit', selected.value)
}
</script>

<style scoped>
.cancel-sub-overlay {
  position: fixed;
  inset: 0;
  z-index: 13000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 24px;
}

.cancel-sub-card {
  width: 530px;
  max-width: 100%;
  height: 248px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.cancel-sub-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}

.cancel-sub-head h2 {
  margin: 0;
  width: 153px;
  height: 24px;
  font-family: SFPro, SFPro;
  font-weight: 600;
  font-size: 16px;
  color: #000000;
  line-height: 24px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cancel-sub-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-top: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  flex-shrink: 0;
}

.cancel-sub-x img {
  width: 14px;
  height: 14px;
  display: block;
}

.cancel-sub-body {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  flex: 1 1 auto;
  min-height: 0;
}

.plan-line {
  margin: 0;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
}

.field-label {
  margin-top: 16px;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
}

.select-wrap {
  position: relative;
  margin-top: 16px;
  width: 482px;
  max-width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  width: 482px;
  max-width: 100%;
  height: 32px;
  padding: 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
}

.select-trigger-text {
  flex: 1 1 auto;
  min-width: 0;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-arrow {
  width: 12px;
  height: 12px;
  object-fit: contain;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.select-arrow.up {
  transform: rotate(180deg);
}

.select-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 2;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 8px 0;
  box-sizing: border-box;
  overflow: hidden;
}

.select-option {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 16px;
  cursor: pointer;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
}

.select-option.selected {
  font-weight: 600;
}

.select-option:hover {
  background: #f7f7f7;
}

.cancel-sub-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  flex-shrink: 0;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  cursor: pointer;
}

.btn-cancel {
  background: #ffffff;
  border: 1px solid #000000;
}

.btn-cancel:hover {
  background: #000000;
}

.btn-cancel .btn-txt {
  width: 48px;
  height: 24px;
  font-family: SFPro, SFPro;
  font-weight: bold;
  font-size: 14px;
  color: #000000;
  line-height: 24px;
  text-align: right;
  font-style: normal;
}

.btn-cancel:hover .btn-txt {
  color: #ffffff;
}

.btn-submit {
  background: #000000;
  border: none;
}

.btn-submit:hover {
  background: #484848;
}

.btn-submit .btn-txt {
  width: 50px;
  height: 24px;
  font-family: SFPro, SFPro;
  font-weight: bold;
  font-size: 14px;
  color: #ffffff;
  line-height: 24px;
  text-align: left;
  font-style: normal;
}
</style>
